package com.mgacreative.hotel.feature.channelmanager.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.feature.channelmanager.data.ChannelConnectionEntity
import com.mgacreative.hotel.feature.channelmanager.data.ChannelManagerRepository
import com.mgacreative.hotel.feature.channelmanager.data.ChannelSyncLogEntity
import com.mgacreative.hotel.feature.channelmanager.domain.ChannelManagerService
import com.mgacreative.hotel.feature.channelmanager.domain.OtaReservation
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class ChannelManagerViewModel(
    val service: ChannelManagerService,
    private val repository: ChannelManagerRepository
) : ViewModel() {

    private val _channels = MutableStateFlow<List<ChannelConnectionEntity>>(emptyList())
    val channels: StateFlow<List<ChannelConnectionEntity>> = _channels.asStateFlow()

    private val _logs = MutableStateFlow<List<ChannelSyncLogEntity>>(emptyList())
    val logs: StateFlow<List<ChannelSyncLogEntity>> = _logs.asStateFlow()

    private val _isLoading = MutableStateFlow(false)
    val isLoading: StateFlow<Boolean> = _isLoading.asStateFlow()

    init {
        loadChannels()
    }

    fun loadChannels() {
        viewModelScope.launch {
            _isLoading.value = true
            try {
                _channels.value = repository.getChannels()
                _logs.value = repository.getLogs()
            } catch (_: Exception) {
            } finally {
                _isLoading.value = false
            }
        }
    }

    fun saveChannel(channel: ChannelConnectionEntity) {
        viewModelScope.launch {
            _isLoading.value = true
            val saved = repository.saveChannel(channel)
            if (saved != null) {
                repository.addLog(
                    channelName = channel.channel_name,
                    actionType = "CHANNEL_CONFIG",
                    message = "${channel.channel_name} bağlantı ve API ayarları kaydedildi.",
                    status = "SUCCESS"
                )
                loadChannels()
            }
            _isLoading.value = false
        }
    }

    fun deleteChannel(channelId: String, channelName: String) {
        viewModelScope.launch {
            _isLoading.value = true
            val success = repository.deleteChannel(channelId)
            if (success) {
                repository.addLog(
                    channelName = channelName,
                    actionType = "CHANNEL_DELETE",
                    message = "$channelName kanalı sistemden silindi.",
                    status = "SUCCESS"
                )
                loadChannels()
            }
            _isLoading.value = false
        }
    }

    fun toggleChannel(channel: ChannelConnectionEntity, newActive: Boolean) {
        viewModelScope.launch {
            val channelId = channel.id ?: return@launch
            _channels.value = _channels.value.map {
                if (it.id == channelId) it.copy(is_active = newActive) else it
            }
            repository.toggleChannelStatus(channelId, newActive)
            val msg = if (newActive) "Kanal bağlantısı aktif edildi." else "Kanal bağlantısı durduruldu (Stop-Sell)."
            repository.addLog(channel.channel_name, "CHANNEL_STATUS", msg, "SUCCESS")
        }
    }

    fun updateMarkup(channel: ChannelConnectionEntity, newMarkup: Double) {
        viewModelScope.launch {
            val channelId = channel.id ?: return@launch
            _channels.value = _channels.value.map {
                if (it.id == channelId) it.copy(markup_percentage = newMarkup) else it
            }
            repository.updateMarkup(channelId, newMarkup)
            repository.addLog(channel.channel_name, "PRICE_SYNC", "Mark-up komisyon oranı %$newMarkup olarak güncellendi.", "SUCCESS")
        }
    }

    fun syncAllChannels() {
        viewModelScope.launch {
            _isLoading.value = true
            val activeChannels = _channels.value.filter { it.is_active }
            activeChannels.forEach { channel ->
                repository.addLog(channel.channel_name, "AVAILABILITY_SYNC", "Müsaitlik ve fiyat periyotları OTA API'ye senkronize edildi.", "SUCCESS")
            }
            loadChannels()
            _isLoading.value = false
        }
    }

    fun testIncomingOtaBooking(channelName: String, roomId: String, date: String) {
        viewModelScope.launch {
            val res = OtaReservation(
                id = "ota_${kotlin.random.Random.nextInt(1000, 9999)}",
                channel = channelName,
                roomId = roomId,
                guestName = "OTA Misafiri (Canlı Test)",
                checkIn = date,
                checkOut = "2026-09-10"
            )
            service.receiveOtaReservation(res)
            repository.addLog(channelName, "INCOMING_BOOKING", "Oda $roomId için $date tarihli rezervasyon başarıyla işlendi.", "SUCCESS")
            loadChannels()
        }
    }
}
